import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { PlusIcon, EyeIcon, TrashIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/20/solid'
import { createCrudApi } from '../../lib/api'
import { AccountForm } from '../../components/forms/AccountForm'
import { AccountTypeBadge } from '../../components/ui/StatusBadge'
import { Avatar } from '../../components/ui/Avatar'
import { Pagination } from '../../components/ui/Pagination'
import { useTranslation } from '../../hooks/useTranslation'
import { useUserLookup } from '../../hooks/useUsers'
import type { Account, AccountInput } from '../../../shared/types'

const accountsApi = createCrudApi<Account, AccountInput>('accounts')

const TYPES = ['customer', 'prospect', 'partner', 'vendor'] as const
type AccountType = typeof TYPES[number]
type SortField = 'name' | 'type' | 'industry' | 'phone' | 'website'
type SortDirection = 'asc' | 'desc'

const TYPE_ORDER: Record<AccountType, number> = {
  customer: 0,
  prospect: 1,
  partner: 2,
  vendor: 3,
}

export function AccountsListPage() {
  const [showForm, setShowForm] = useState(false)
  const [typeFilter, setTypeFilter] = useState<AccountType | 'all'>('all')
  const [sortField, setSortField] = useState<SortField>('name')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { getUser } = useUserLookup()

  // Build filters object for API
  const filters = typeFilter !== 'all' ? { type: typeFilter } : undefined

  const { data: response, isLoading } = useQuery({
    queryKey: ['accounts', { page, limit, filters }],
    queryFn: () => accountsApi.list({ page, limit, filters }),
  })

  const accounts = response?.data

  // Handle filter change - reset to page 1
  const handleTypeFilter = (type: AccountType | 'all') => {
    setTypeFilter(type)
    setPage(1)
  }

  const createMutation = useMutation({
    mutationFn: accountsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      setShowForm(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: accountsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
    },
  })

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  // Client-side sorting only (filtering is now server-side)
  const sortedAccounts = useMemo(() => {
    if (!accounts) return []

    const result = [...accounts]

    result.sort((a, b) => {
      let comparison = 0

      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name)
          break
        case 'type':
          comparison = (TYPE_ORDER[a.type as AccountType] ?? 99) - (TYPE_ORDER[b.type as AccountType] ?? 99)
          break
        case 'industry':
          comparison = (a.industry || '').localeCompare(b.industry || '')
          break
        case 'phone':
          comparison = (a.phone || '').localeCompare(b.phone || '')
          break
        case 'website':
          comparison = (a.website || '').localeCompare(b.website || '')
          break
      }

      return sortDirection === 'asc' ? comparison : -comparison
    })

    return result
  }, [accounts, sortField, sortDirection])

  const SortableHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => {
    const isActive = sortField === field
    return (
      <th
        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 select-none"
        onClick={() => handleSort(field)}
      >
        <div className="flex items-center gap-1">
          {children}
          <div className="flex flex-col -space-y-1">
            <ChevronUpIcon
              className={`h-3 w-3 ${isActive && sortDirection === 'asc' ? 'text-gray-900' : 'text-gray-300'}`}
            />
            <ChevronDownIcon
              className={`h-3 w-3 ${isActive && sortDirection === 'desc' ? 'text-gray-900' : 'text-gray-300'}`}
            />
          </div>
        </div>
      </th>
    )
  }

  if (isLoading) return <div>{t('common.loading')}</div>

  return (
    <>
      {/* Secondary column - Actions and filters */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('pages.accounts.title')}</h2>
            <p className="mt-1 text-sm text-gray-500">
              {t('pages.accounts.description')}
            </p>
          </div>

          <button
            type="button"
            onClick={() => setShowForm(!showForm)}
            className="w-full flex items-center justify-center gap-x-2 rounded-md px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:opacity-90 bg-brand-500"
          >
            <PlusIcon className="h-5 w-5" />
            {showForm ? t('common.cancel') : t('pages.accounts.addButton')}
          </button>

          {showForm && (
            <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
              <h3 className="text-sm font-medium text-gray-900 mb-4">{t('pages.accounts.newTitle')}</h3>
              <AccountForm
                onSubmit={(data) => createMutation.mutate(data)}
                onCancel={() => setShowForm(false)}
                loading={createMutation.isPending}
              />
            </div>
          )}

          {/* Type Filter */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('common.filter')} {t('fields.type')}</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleTypeFilter('all')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  typeFilter === 'all'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t('common.all')}
              </button>
              {TYPES.map(type => {
                const typeLabels: Record<AccountType, string> = {
                  customer: t('status.customer'),
                  prospect: t('status.prospect'),
                  partner: t('status.partner'),
                  vendor: t('status.vendor'),
                }
                return (
                  <button
                    key={type}
                    onClick={() => handleTypeFilter(type)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                      typeFilter === type
                        ? 'bg-gray-900 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {typeLabels[type]}
                  </button>
                )
              })}
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('pages.accounts.stats.title')}</h3>
            <dl className="space-y-2">
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{t('pages.accounts.stats.total')}</dt>
                <dd className="font-medium text-gray-900">{response?.total || 0}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{t('pagination.page')}</dt>
                <dd className="font-medium text-gray-900">{response?.page || 1} {t('pagination.of')} {response?.totalPages || 1}</dd>
              </div>
            </dl>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] px-4 py-10 sm:px-6 lg:px-8 lg:py-6 overflow-hidden">
          {/* Mobile header */}
          <div className="xl:hidden mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">{t('pages.accounts.title')}</h1>
            <button
              type="button"
              onClick={() => setShowForm(!showForm)}
              className="mt-4 w-full flex items-center justify-center gap-x-2 rounded-md px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:opacity-90 bg-brand-500"
            >
              <PlusIcon className="h-5 w-5" />
              {showForm ? t('common.cancel') : t('pages.accounts.addButton')}
            </button>
            {showForm && (
              <div className="mt-6 bg-white p-6 rounded-lg shadow">
                <h2 className="text-lg font-medium mb-4">{t('pages.accounts.newTitle')}</h2>
                <AccountForm
                  onSubmit={(data) => createMutation.mutate(data)}
                  onCancel={() => setShowForm(false)}
                  loading={createMutation.isPending}
                />
              </div>
            )}
          </div>

          {/* Table */}
          <div className="flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <div className="overflow-hidden shadow-sm ring-1 ring-gray-900/5 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <SortableHeader field="name">
                          <span className="pl-4 sm:pl-6">{t('fields.name')}</span>
                        </SortableHeader>
                        <SortableHeader field="type">
                          {t('fields.type')}
                        </SortableHeader>
                        <SortableHeader field="industry">
                          {t('fields.industry')}
                        </SortableHeader>
                        <SortableHeader field="phone">
                          {t('fields.phone')}
                        </SortableHeader>
                        <SortableHeader field="website">
                          {t('fields.website')}
                        </SortableHeader>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('fields.owner')}
                        </th>
                        <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                          <span className="sr-only">{t('common.actions')}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {sortedAccounts.map((account) => (
                        <tr key={account.id} className="hover:bg-gray-50 transition-colors">
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                            {account.name}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {account.type ? <AccountTypeBadge type={account.type} /> : '-'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {account.industry || '-'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {account.phone || '-'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {account.website || '-'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {(() => {
                              const owner = getUser(account.ownerId)
                              if (!owner) return '-'
                              return (
                                <div className="flex items-center gap-x-2">
                                  <Avatar firstName={owner.firstName} lastName={owner.lastName} size="xs" />
                                  <span>{owner.firstName} {owner.lastName}</span>
                                </div>
                              )
                            })()}
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <Link
                              to={`/accounts/${account.id}`}
                              className="inline-flex items-center gap-x-1 text-brand-600 hover:text-brand-900 mr-4"
                            >
                              <EyeIcon className="h-4 w-4" />
                              {t('common.view')}
                            </Link>
                            <button
                              onClick={() => deleteMutation.mutate(account.id)}
                              className="inline-flex items-center gap-x-1 text-red-600 hover:text-red-900"
                            >
                              <TrashIcon className="h-4 w-4" />
                              {t('common.delete')}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {response && (
                    <Pagination
                      page={response.page}
                      totalPages={response.totalPages}
                      total={response.total}
                      limit={response.limit}
                      onPageChange={setPage}
                      onLimitChange={(newLimit) => {
                        setLimit(newLimit)
                        setPage(1)
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
